import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, Download, Upload, RefreshCw, Trash2, Building2, Globe, ChevronDown, ChevronUp, Ruler } from 'lucide-react';
import { useMfrs, fetchRegistry, downloadMfr, deleteMfr, setMfrItem, exportTemplate, importTemplate, getSeen, markSeen, publishMfr, searchMfr } from '../lib/mfr';
import { SECTORS } from '../lib/catalog';
import { isAdmin } from '../lib/license';
import { openPond } from './FirePondPlanner';
import { assetStatus, cacheAssets, removeAssets } from '../lib/mfrAssets';
import { getContact } from '../lib/license';
import { useDealers, addDealer, delDealer } from '../lib/dealers';

const AUTO = new Set();
export const openMfr = () => window.dispatchEvent(new Event('giss-mfr-open'));
const dec = (v) => (v === '' ? null : Number.isFinite(+String(v).replace(',', '.')) ? +String(v).replace(',', '.') : null);

function Registry({ t, mine }) {
  const [reg, setReg] = useState(null);
  const [busy, setBusy] = useState('');
  const [seen] = useState(getSeen);
  useEffect(() => { fetchRegistry().then((r) => { setReg(r); markSeen(r.map((x) => x.id)); }).catch(() => setReg(false)); }, []);
  const load = async (id) => { setBusy(id); try { await downloadMfr(id); toast.success(t.mfLoaded); AUTO.add(id); window.dispatchEvent(new CustomEvent('giss-mfr-assets', { detail: id })); } catch { toast.error(t.mfOffline); } setBusy(''); };
  return (
    <section className="mfr-sec" data-testid="mfr-registry">
      <h5><Globe size={15} /> {t.mfRegistry}</h5>
      <p className="hint">{t.mfRegistryHint}</p>
      {reg === false && <div className="empty" data-testid="mfr-registry-offline">{t.mfOffline}</div>}
      {(reg || []).map((m) => (
        <div key={m.id} className="mfr-row" data-testid={`mfr-reg-${m.id}`}>
          <span><b>{m.name}</b> {!seen.includes(m.id) && <em className="mfr-new" data-testid={`mfr-new-${m.id}`}>{t.mfNew}</em>}<small>{[m.city, `${m.count} ${t.mfItems}`, m.version].filter(Boolean).join(' · ')}</small></span>
          {m.url && <a href={m.url} target="_blank" rel="noreferrer" className="icon-btn"><Globe size={14} /></a>}
          <button className="btn btn-dark btn-sm" disabled={busy === m.id} data-testid={`mfr-load-${m.id}`} onClick={() => load(m.id)}>
            {mine[m.id] ? <><RefreshCw size={14} /> {t.mfUpdate}</> : <><Download size={14} /> {t.mfLoad}</>}
          </button>
        </div>
      ))}
      <VendorBox t={t} />
    </section>
  );
}

function AssetBar({ t, d }) {
  const [st, setSt] = useState(null);
  const [prog, setProg] = useState(null);
  useEffect(() => { assetStatus(d).then((x) => { setSt(x); if (AUTO.has(d.id)) { AUTO.delete(d.id); window.dispatchEvent(new CustomEvent('giss-mfr-assets', { detail: d.id })); } }); }, [d]);
  const run = async () => {
    if (prog) return;
    setProg([0, st?.total || 0]);
    const r = await cacheAssets(d, (a, b) => setProg([a, b]));
    setProg(null); setSt(await assetStatus(d));
    if (r.fail) toast.error(t.maFail.replace('{n}', r.fail)); else toast.success(t.maDone);
  };
  useEffect(() => { const on = (e) => { if (e.detail === d.id) { AUTO.delete(d.id); run(); } }; window.addEventListener('giss-mfr-assets', on); return () => window.removeEventListener('giss-mfr-assets', on); });
  if (!st || !st.total) return null;
  const full = st.have >= st.total;
  return (
    <div className="mfr-assets" data-testid={`mfr-assets-${d.id}`}>
      <span data-testid={`mfr-assets-status-${d.id}`}>{prog ? `${t.maLoading} ${prog[0]} / ${prog[1]}` : `${full ? '✅' : '⬇️'} ${t.maStatus}: ${st.have} / ${st.total}`}</span>
      {prog && <i className="mfr-assets-bar"><b style={{ width: `${(100 * prog[0]) / Math.max(1, prog[1])}%` }} /></i>}
      {!prog && !full && <button className="btn btn-green btn-sm" data-testid={`mfr-assets-btn-${d.id}`} onClick={run}><Download size={14} /> {t.maSave}</button>}
    </div>
  );
}

function VendorBox({ t }) {
  const c = getContact();
  const mail = () => { window.location.href = `mailto:${c.email || ''}?subject=${encodeURIComponent(t.mvSubj)}&body=${encodeURIComponent(t.mvBody)}`; };
  return (
    <div className="mfr-vendor" data-testid="mfr-vendor-box">
      <span><b>{t.mvTitle}</b><small>{t.mvText}</small></span>
      <button className="btn btn-proj btn-sm" data-testid="mfr-vendor-btn" onClick={mail}>{t.mvBtn}</button>
    </div>
  );
}

function DealerBox({ t }) {
  const dl = useDealers();
  const [f, setF] = useState({ name: '', email: '', phone: '' });
  const add = () => { if (!f.name.trim()) return; addDealer(f); setF({ name: '', email: '', phone: '' }); toast.success(t.dlAdded); };
  return (
    <section className="mfr-sec" data-testid="mfr-dealers">
      <h5>🏪 {t.dlTitle}</h5>
      <p className="hint">{t.dlHint}</p>
      {dl.list.map((d) => (
        <div key={d.id} className="mfr-row" data-testid={`dealer-${d.id}`}>
          <span><b>{d.name}</b><small>{[d.email, d.phone].filter(Boolean).join(' · ')}</small></span>
          <button className="icon-btn danger" data-testid={`dealer-del-${d.id}`} onClick={() => window.confirm(t.dlDel) && delDealer(d.id)}><Trash2 size={14} /></button>
        </div>
      ))}
      <div className="row dealer-form">
        {['name', 'email', 'phone'].map((k) => <input key={k} data-testid={`dealer-${k}`} placeholder={t['dl_' + k]} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />)}
        <button className="btn btn-dark btn-sm" data-testid="dealer-add-btn" onClick={add}>+ {t.dlAdd}</button>
      </div>
    </section>
  );
}

function MineRow({ t, d }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const list = open ? searchMfr(d.items.map((x) => ({ ...x, mfr: d.name })), q).slice(0, 120) : [];
  const publish = async () => { try { await publishMfr(d, localStorage.getItem('giss_owner_token')); toast.success(t.mfPublished); } catch { toast.error('Admin'); } };
  return (
    <div className="mfr-mine" data-testid={`mfr-mine-${d.id}`}>
      <div className="mfr-row">
        <span><b>{d.name}</b><small>{`${d.items.length} ${t.mfItems}${d.note ? ' · ' + d.note : ''}`}</small></span>
        <button className="btn btn-outline btn-sm" data-testid={`mfr-show-${d.id}`} onClick={() => setOpen(!open)}>{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {open ? t.mfHide : t.mfShow}</button>
        {isAdmin() && d.id.startsWith('own-') && <button className="btn btn-proj btn-sm" data-testid={`mfr-publish-${d.id}`} onClick={publish}>{t.mfPublish}</button>}
        <button className="icon-btn danger" data-testid={`mfr-del-${d.id}`} onClick={() => { if (window.confirm(t.mfDelete)) { removeAssets(d); deleteMfr(d.id); } }}><Trash2 size={14} /></button>
      </div>
      <AssetBar t={t} d={d} />
      {open && <>
        <input className="mfr-q" data-testid={`mfr-q-${d.id}`} value={q} placeholder={t.mfSearch} onChange={(e) => setQ(e.target.value)} />
        <table className="mat-table mfr-table" data-testid={`mfr-table-${d.id}`}>
          <thead><tr><th>{t.mfColName}</th><th>{t.mfColDim}</th><th>{t.mfColSku}</th><th>{t.mfColPrice}</th></tr></thead>
          <tbody>{list.map((x) => (
            <tr key={x.id}>
              <td>{x.url ? <a href={x.url} target="_blank" rel="noreferrer">{x.name}</a> : x.name}<small> {x.mat}</small></td>
              <td>{[x.dim, x.pn].filter(Boolean).join(' · ')}</td>
              <td><input className="rm-num" data-testid={`mfr-sku-${x.id}`} defaultValue={x.sku} onBlur={(e) => e.target.value !== x.sku && setMfrItem(d.id, x.id, { sku: e.target.value })} /></td>
              <td><input className="rm-num" inputMode="decimal" data-testid={`mfr-price-${x.id}`} placeholder={t.mfOnReq} defaultValue={x.price ?? ''} onBlur={(e) => setMfrItem(d.id, x.id, { price: dec(e.target.value) })} /></td>
            </tr>
          ))}</tbody>
        </table>
      </>}
    </div>
  );
}

function ExcelBox({ t }) {
  const [sec, setSec] = useState('water');
  const ref = useRef(null);
  const onFile = async (e) => {
    const f = e.target.files[0]; e.target.value = '';
    if (!f) return;
    try { const r = await importTemplate(t, f); toast.success(t.mfImported.replace('{n}', r.n).replace('{m}', r.m)); } catch { toast.error(t.mfImportErr); }
  };
  return (
    <section className="mfr-sec" data-testid="mfr-excel">
      <h5>📊 {t.mfExcel}</h5>
      <p className="hint">{t.mfExcelHint}</p>
      <div className="row">
        <select data-testid="mfr-excel-sector" value={sec} onChange={(e) => setSec(e.target.value)}>{SECTORS.map((s) => <option key={s} value={s}>{t['sec_' + s]}</option>)}</select>
        <button className="btn btn-dark btn-sm" data-testid="mfr-excel-export" onClick={() => exportTemplate(t, sec)}><Download size={14} /> {t.mfExport}</button>
        <button className="btn btn-outline btn-sm" data-testid="mfr-excel-export-all" onClick={() => exportTemplate(t, 'all')}><Download size={14} /> {t.mfExportAll}</button>
        <button className="btn btn-green btn-sm" data-testid="mfr-excel-import" onClick={() => ref.current.click()}><Upload size={14} /> {t.mfImport}</button>
        <input ref={ref} hidden type="file" accept=".xlsx,.xls,.csv" data-testid="mfr-excel-input" onChange={onFile} />
      </div>
    </section>
  );
}

export default function MfrDialog({ t, wide }) {
  const [open, setOpen] = useState(false);
  const mine = useMfrs();
  useEffect(() => { const on = () => setOpen(true); window.addEventListener('giss-mfr-open', on); return () => window.removeEventListener('giss-mfr-open', on); }, []);
  if (!open) return null;
  return createPortal(
    <div className="editor-backdrop" data-testid="mfr-dialog">
      <div className="editor mfr-dialog">
        <div className="editor-head">
          <h4><Building2 size={18} /> {t.mfTitle}</h4>
          {wide && <button className="btn btn-proj btn-sm" data-testid="mfr-open-planner" onClick={() => { setOpen(false); openPond(); }}><Ruler size={14} /> {t.fpOpen}</button>}
          <button className="icon-btn" data-testid="mfr-close-btn" onClick={() => setOpen(false)}><X size={16} /></button>
        </div>
        <Registry t={t} mine={mine} />
        <section className="mfr-sec" data-testid="mfr-mine">
          <h5>📦 {t.mfMine}</h5>
          {Object.values(mine).length ? Object.values(mine).map((d) => <MineRow key={d.id} t={t} d={d} />) : <div className="empty">{t.mfNone}</div>}
        </section>
        <DealerBox t={t} />
        <ExcelBox t={t} />
      </div>
    </div>,
    document.body,
  );
}
